import {get, post} from "aws-amplify/api";
import {
  TokenBalance,
  TokenPricing,
  PaymentHistory,
  PurchaseRequest,
  PurchaseResult,
  PaymentError,
  PaginationParams
} from "../types/payment";

export class PaymentService {
  /**
   * Get current token balance for the authenticated user
   */
  async getTokenBalance(jwtToken: string): Promise<TokenBalance> {
    try {
      if (!jwtToken) {
        throw this.createError("AUTH_ERROR", "User not authenticated");
      }

      const params = {
        headers: {
          Authorization: jwtToken
        }
      };

      const {body} = await get({
        apiName: "app",
        path: "/tokens/balance",
        options: params
      }).response;

      return (await body.json()) as unknown as TokenBalance;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Get token pricing information (public - no auth required)
   */
  async getTokenPricing(jwtToken?: string): Promise<TokenPricing> {
    try {
      // Build headers object conditionally to avoid TypeScript errors
      const headers: Record<string, string> = {
        "Content-Type": "application/json"
      };

      if (jwtToken) {
        headers.Authorization = jwtToken;
      }

      const params = {
        headers
      };

      const {body} = await get({
        apiName: "data",
        path: "/tokens/pricing",
        options: params
      }).response;

      return (await body.json()) as unknown as TokenPricing;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Get payment history with pagination
   */
  async getPaymentHistory(jwtToken: string, pagination: PaginationParams): Promise<PaymentHistory> {
    try {
      if (!jwtToken) {
        throw this.createError("AUTH_ERROR", "User not authenticated");
      }

      const params = {
        headers: {
          Authorization: jwtToken
        },
        queryParams: {
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString()
        }
      };

      const {body} = await get({
        apiName: "app",
        path: "/tokens/history",
        options: params
      }).response;

      return (await body.json()) as unknown as PaymentHistory;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Create a Stripe checkout session for token purchase
   */
  async purchaseTokens(jwtToken: string, request: PurchaseRequest): Promise<PurchaseResult> {
    try {
      if (!jwtToken) {
        throw this.createError("AUTH_ERROR", "User not authenticated");
      }

      const params: any = {
        headers: {
          Authorization: jwtToken,
          "Content-Type": "application/json"
        },
        body: request // Pass object directly, AWS Amplify will stringify it
      };

      const {body} = await post({
        apiName: "app",
        path: "/payment/checkout",
        options: params
      }).response;

      const result = (await body.json()) as unknown as PurchaseResult;

      // Redirect to Stripe Checkout
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }

      return result;
    } catch (error) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Retry a failed operation with exponential backoff
   */
  async retryOperation<T>(operation: () => Promise<T>, maxAttempts: number = 3): Promise<T> {
    let lastError: PaymentError;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as PaymentError;

        if (!lastError.retryable || attempt === maxAttempts) {
          throw lastError;
        }

        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  /**
   * Handle API errors and convert to PaymentError
   */
  private handleApiError(error: any): PaymentError {
    console.error("Payment API error:", error);

    // Check for CORS errors
    if (error.message && error.message.toLowerCase().includes("cors")) {
      return this.createError("NETWORK_ERROR", "CORS error - API configuration issue", false);
    }

    if (error.response) {
      // API returned error response
      const status = error.response.status;

      switch (status) {
        case 401:
          return this.createError("AUTH_ERROR", "Authentication required", false);
        case 402:
          return this.createError("INSUFFICIENT_FUNDS", "Insufficient token balance", false);
        case 500:
          return this.createError("PAYMENT_FAILED", "Server error occurred", true);
        default:
          return this.createError("PAYMENT_FAILED", `API error: ${status}`, false);
      }
    } else if (error.code === "NetworkError") {
      return this.createError("NETWORK_ERROR", "Network connection failed", false);
    } else {
      return this.createError("PAYMENT_FAILED", error.message || "Unknown error", false);
    }
  }

  /**
   * Create a structured PaymentError
   */
  private createError(
    code: PaymentError["code"],
    message: string,
    retryable: boolean = false,
    retryAfter?: number
  ): PaymentError {
    return {
      code,
      message,
      retryable,
      retryAfter
    };
  }
}

// Export singleton instance
export const paymentService = new PaymentService();
